import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import { loadingScreenViews } from "../../modules/playEngineExportTargets";

import SelectorBases from "./selectorBases";
import RuntimeBasesPanel from "./runtimeBasesPanel";
import RuntimeGamesPanel from "./runtimeGamesPanel";
import Pe3dGamesPanel from "./pe3dGamesPanel";
import CompanyLogoPicker from "../companyLogoPicker";
import { adminScreenFrame, DANGER, SEARCH_INPUT_PROPS } from "./settingsPalette";
import {
  findCompanies,
  insertCompany,
  updateCompany,
  findGrantableBases,
  switchCompany,
  uploadCompanyLogo,
} from "../../modules/adminApi";

/**
 * SuperAdmin panel: companies, and which bases each one may work on.
 *
 * Creating a company and granting it bases used to mean a curl call carrying a
 * shared secret, then editing mongo by hand. The base grant is the load-bearing
 * part — it is what decides which games a company's users see, and which
 * snapshot editors they can open, so it is the first tab and gets the room.
 *
 * Runtime games and bases have their own views, both reachable directly from
 * the Companies & Games header. Users granted manageRuntimeBases also get the
 * runtime screens as a page of their own, starting with bases.
 *
 * Layout mirrors User Management: a fixed-height page, a list that scrolls on
 * its own, a tabbed editor, a pinned action bar. Creating a company happens in
 * the editor pane rather than crammed under the list, which is where it used to
 * sit — a five-field form directly below a scrolling list, with no telling where
 * one ended and the other began.
 *
 * `grantOnly` is the same screen for a user a superAdmin gave
 * grantBasesToCompanies: titled "Companies", the base grant and nothing else —
 * no company creation, no runtime bases (those are their own page, when
 * granted), no Settings tab (the server keeps those fields superAdmin-only), and
 * "View dashboard as" only for a company the user may switch into.
 */

const useStyles = makeStyles(() => ({
  ...adminScreenFrame,

  // Internal tools is the one setting on this screen that reaches outside the
  // company it is set on, so it is the one thing drawn as a bounded object. The
  // rim is the warning; there is no filled panel, which at this size would read
  // as an error state rather than a switch to be careful with.
  dangerBlock: {
    marginTop: "0.5rem",
    padding: "0.75rem 1rem",
    border: `1px solid ${DANGER}`,
    borderRadius: "4px",
  },
  dangerTitle: { fontSize: "0.72rem", letterSpacing: 0.5, textTransform: "uppercase", color: DANGER },

  // A bare switch with no explanatory second line, unlike the checkRow used for
  // permissions — the paragraph above it has already done that job.
  toggleRow: { display: "flex", alignItems: "center", cursor: "pointer" },
  toggleLabel: { fontSize: "0.82rem" },
}));

const NEW_COMPANY = "__new__";
const TAB = { BASES: 0, SETTINGS: 1 };
const VIEW = { COMPANIES: "companies", BASES: "bases", GAMES: "games", PE3D: "pe3d" };

export default function SuperAdminPanel(props) {
  const classes = useStyles();

  const [companies, setCompanies] = useState([]);
  const [bases, setBases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompanyID, setSelectedCompanyID] = useState(null);
  const [grantedBaseIDs, setGrantedBaseIDs] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB.BASES);
  const [query, setQuery] = useState("");

  const [newCompanyName, setNewCompanyName] = useState("");
  const [newAdminUsername, setNewAdminUsername] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  // Held until the company exists — there is nothing to attach it to before that.
  const [newCompanyLogoFile, setNewCompanyLogoFile] = useState(null);
  const [createdCompanyCount, setCreatedCompanyCount] = useState(0);
  // Decides the download limits shown in Account Info and Billing. Most
  // companies sit on FREE, so that is where a new one starts.
  const [newCompanyPlan, setNewCompanyPlan] = useState("FREE");

  const [view, setView] = useState(VIEW.COMPANIES);

  const grantOnly = props.grantOnly === true;
  // A narrowed base granter is listed only its own bases, while the grant also
  // holds others it cannot see (and the server leaves alone). Count what is listed.
  const listedBaseIDs = useMemo(() => new Set(bases.map((base) => String(base._id))), [bases]);
  const countListed = (baseIDs) => (baseIDs || []).filter((id) => listedBaseIDs.has(String(id))).length;
  const listedGrantCount = countListed(grantedBaseIDs);
  const isCreating = selectedCompanyID === NEW_COMPANY;
  const selectedCompany = companies.find((company) => company._id === selectedCompanyID) || null;
  // Mirrors roles.canSwitchToCompany, so the button is never offered where the
  // switch would be refused.
  const canViewAs =
    Boolean(selectedCompany) &&
    (!grantOnly ||
      String(selectedCompany._id) === String(localStorage.getItem("user.homeCompanyID")) ||
      (props.switchTargets || []).some((company) => String(company._id) === String(selectedCompany._id)));

  const visibleCompanies = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return companies;
    return companies.filter((company) => (company.name || "").toLowerCase().includes(needle));
  }, [companies, query]);

  const reload = async (keepSelection) => {
    const [companyResult, baseResult] = await Promise.all([findCompanies({}), findGrantableBases()]);

    if (!companyResult.ok) {
      props.createSnackbar("Could not load companies.", "error", false);
      setIsLoading(false);
      return;
    }
    setCompanies(companyResult.data || []);
    if (baseResult.ok) {
      setBases(baseResult.data || []);
    } else {
      props.createSnackbar("Could not load the base list.", "error", false);
    }

    const nextID = keepSelection || (companyResult.data || [])[0]?._id || null;
    setSelectedCompanyID(nextID);
    const next = (companyResult.data || []).find((company) => company._id === nextID);
    setGrantedBaseIDs(next ? next.bases || [] : []);
    setIsLoading(false);
  };

  useEffect(() => {
    reload();
  }, []);

  const selectCompany = (company) => {
    setSelectedCompanyID(company._id);
    setGrantedBaseIDs(company.bases || []);
    setActiveTab(TAB.BASES);
  };

  const startNewCompany = () => {
    setSelectedCompanyID(NEW_COMPANY);
    setGrantedBaseIDs([]);
  };

  const saveGrant = async () => {
    if (!selectedCompany) return;
    setIsSaving(true);
    // An empty grant is meaningful — the company then sees nothing — but a form
    // encoder cannot send an empty array: appending no values just omits the
    // field, which the server reads as "leave it alone". An empty string is the
    // agreed way to say "set it to nothing" (see userRequestFields.js).
    const result = await updateCompany({
      companyID: selectedCompany._id,
      bases: grantedBaseIDs.length > 0 ? grantedBaseIDs : "",
    });
    setIsSaving(false);

    if (!result.ok) {
      props.createSnackbar(`Could not save: ${result.data || result.status}`, "error", false);
      return;
    }
    props.createSnackbar(`${selectedCompany.name}: ${listedGrantCount} bases granted.`, "success", false);
    reload(selectedCompany._id);
  };

  const createCompany = async () => {
    if (newCompanyName.trim().length < 2) {
      props.createSnackbar("A company name needs at least 2 characters.", "warning", false);
      return;
    }
    if (newAdminUsername && newAdminPassword.length < 8) {
      props.createSnackbar("An admin password needs at least 8 characters.", "warning", false);
      return;
    }

    setIsSaving(true);
    const result = await insertCompany({
      name: newCompanyName.trim(),
      plan: newCompanyPlan,
      adminUsername: newAdminUsername || undefined,
      adminPassword: newAdminPassword || undefined,
      adminPermissions: newAdminUsername
        ? ["viewContent", "editContent", "manageAssets", "createVersion", "deleteContent", "exportPlayable"]
        : undefined,
    });
    setIsSaving(false);

    if (!result.ok) {
      props.createSnackbar(`Could not create the company: ${result.data || result.status}`, "error", false);
      return;
    }
    const createdID = result.data && result.data._id;
    if (newCompanyLogoFile && createdID) {
      const logoResult = await uploadCompanyLogo(createdID, newCompanyLogoFile);
      if (!logoResult.ok) {
        // The company is already made; losing the logo is not worth failing over.
        props.createSnackbar("Company created, but the logo did not upload.", "warning", false);
      }
    }
    props.createSnackbar(`${newCompanyName} created. Grant it some bases next.`, "success", false);
    setNewCompanyName("");
    setNewAdminUsername("");
    setNewAdminPassword("");
    setNewCompanyLogoFile(null);
    setCreatedCompanyCount((count) => count + 1);
    setActiveTab(TAB.BASES);
    reload(createdID);
  };

  // The loading screen every playEngine export of this company is built with.
  // It is set here rather than in the export dialog so a customer cannot change
  // the screen their playables ship with; the export controllers read it off the
  // company document and ignore anything the client sends.
  const setLoadingScreen = async (screenID) => {
    if (!selectedCompany) return;
    setIsSaving(true);
    const result = await updateCompany({ companyID: selectedCompany._id, playEngineLoadingScreen: screenID });
    setIsSaving(false);
    if (!result.ok) {
      props.createSnackbar(`Could not save: ${result.data || result.status}`, "error", false);
      return;
    }
    const caption = (loadingScreenViews.find((screen) => screen.id === screenID) || {}).caption || screenID;
    props.createSnackbar(`${selectedCompany.name} now exports with ${caption}.`, "success", false);
    reload(selectedCompany._id);
  };

  const setInternalTools = async (isOn) => {
    if (!selectedCompany) return;
    setIsSaving(true);
    const result = await updateCompany({ companyID: selectedCompany._id, hasInternalTools: isOn });
    setIsSaving(false);
    if (!result.ok) {
      props.createSnackbar(`Could not save: ${result.data || result.status}`, "error", false);
      return;
    }
    props.createSnackbar(
      isOn
        ? `${selectedCompany.name} can now use Version Transfer and Version Update.`
        : `${selectedCompany.name} no longer has the internal tools.`,
      "success",
      false
    );
    reload(selectedCompany._id);
  };

  const viewAsCompany = async () => {
    if (!selectedCompany) return;
    const result = await switchCompany(selectedCompany._id);
    if (!result.ok) {
      props.createSnackbar("Could not switch to that company.", "error", false);
      return;
    }
    localStorage.removeItem("selectedGameID");
    localStorage.removeItem("selectedBaseID");
    localStorage.removeItem("selectedVersionID");
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className={classes.root}>
        <div className={classes.centred}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (view === VIEW.PE3D) {
    return (
      <Pe3dGamesPanel
        createSnackbar={props.createSnackbar}
        // A pe3d base made there is a runtime base like any other.
        onBaseCreated={() => {
          reload(selectedCompanyID);
          if (props.onRuntimeChanged) props.onRuntimeChanged();
        }}
        headerAction={
          <Button variant="outlined" onClick={() => setView(VIEW.COMPANIES)}>
            Companies
          </Button>
        }
      />
    );
  }

  if (view === VIEW.GAMES) {
    return (
      <RuntimeGamesPanel
        canDelete
        createSnackbar={props.createSnackbar}
        onShowBases={() => setView(VIEW.BASES)}
        headerAction={
          <Button variant="outlined" onClick={() => setView(VIEW.COMPANIES)}>
            Companies
          </Button>
        }
      />
    );
  }

  if (view === VIEW.BASES) {
    return (
      <RuntimeBasesPanel
        canDelete
        canGrantGames
        createSnackbar={props.createSnackbar}
        // A base created or deleted there changes the grant picker here.
        onChanged={() => reload(selectedCompanyID)}
        onRuntimeChanged={props.onRuntimeChanged}
        onShowGames={() => setView(VIEW.GAMES)}
        headerAction={
          <Button variant="outlined" onClick={() => setView(VIEW.COMPANIES)}>
            Companies
          </Button>
        }
      />
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>{grantOnly ? "Companies" : "Companies & Games"}</Typography>
        <div className={classes.headerSpacer} />
        {!grantOnly && (
          <>
            <Button variant="outlined" onClick={() => setView(VIEW.GAMES)}>
              Runtime games
            </Button>
            <Button variant="outlined" onClick={() => setView(VIEW.BASES)}>
              Runtime bases
            </Button>
            <Button variant="outlined" onClick={() => setView(VIEW.PE3D)}>
              PE3D games
            </Button>
            <Button variant="contained" color="primary" onClick={startNewCompany}>
              + New company
            </Button>
          </>
        )}
      </div>

      <div className={classes.body}>
        <div className={classes.listColumn}>
          <div className={classes.listHeader}>
            <div className={`${classes.listLabel} ${classes.listLabelSpaced}`}>
            Companies ({companies.length})
            </div>
            <TextField
              fullWidth
              size="small"
              placeholder="Search companies"
              InputProps={SEARCH_INPUT_PROPS}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className={classes.listScroller}>
            {visibleCompanies.map((company) => (
              <div
                key={company._id}
                className={`${classes.listRow} ${
                  company._id === selectedCompanyID ? classes.listRowSelected : ""
                }`}
                onClick={() => selectCompany(company)}
              >
                <div>
                  <div className={classes.rowTitle}>
                    {company.name}
                    {/* The same badge language as the user list: a state worth
                      spotting gets a chip, everything ordinary gets none. */}
                    {company.hasInternalTools && (
                      <span className={`${classes.chip} ${classes.chipOutline}`}>internal</span>
                    )}
                  </div>
                  <div className={classes.rowMeta}>
                    {countListed(company.bases)} base{countListed(company.bases) === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.detailColumn}>
          {!selectedCompany && !isCreating && <div className={classes.detailEmpty}>Select a company.</div>}

          {isCreating && (
            <>
              <Typography className={classes.detailTitle}>New company</Typography>
              <div className={classes.detailRule} />
              <div className={classes.tabBody}>
                <TextField
                  className={classes.field}
                  size="small"
                  label="Company name"
                  value={newCompanyName}
                  onChange={(event) => setNewCompanyName(event.target.value)}
                />
                <TextField
                  select
                  className={classes.field}
                  size="small"
                  label="Plan"
                  value={newCompanyPlan}
                  onChange={(event) => setNewCompanyPlan(event.target.value)}
                >
                  {(props.plans || []).map((plan) => (
                    <MenuItem key={plan.type} value={plan.type}>
                      {plan.type}
                    </MenuItem>
                  ))}
                </TextField>

                <Typography className={classes.subTitle}>Logo</Typography>
                <div className={classes.block}>
                  <CompanyLogoPicker
                    inputId="newCompanyLogoInput"
                    buttonLabel="Choose logo"
                    onFileSelected={setNewCompanyLogoFile}
                    resetToken={createdCompanyCount}
                  />
                </div>

                <Typography className={classes.subTitle}>First admin</Typography>
                <Typography className={classes.hint}>
                Optional. Leave blank to create the company on its own and add users later.
                </Typography>
                <TextField
                  className={classes.field}
                  size="small"
                  label="Admin username"
                  value={newAdminUsername}
                  onChange={(event) => setNewAdminUsername(event.target.value)}
                />
                {/* Not masked — see userManagement: the password is being set for
                  someone else and has to be readable to be handed over. */}
                <TextField
                  className={classes.field}
                  size="small"
                  label="Admin password"
                  value={newAdminPassword}
                  onChange={(event) => setNewAdminPassword(event.target.value)}
                />
              </div>
              <div className={classes.footer}>
                <Button variant="contained" color="primary" disabled={isSaving} onClick={createCompany}>
                Create company
                </Button>
                <Button variant="outlined" onClick={() => reload(companies[0] && companies[0]._id)}>
                Cancel
                </Button>
              </div>
            </>
          )}

          {selectedCompany && !isCreating && (
            <>
              <Typography className={classes.detailTitle}>{selectedCompany.name}</Typography>

              <Tabs
                className={classes.tabsBar}
                value={activeTab}
                onChange={(event, next) => setActiveTab(next)}
                indicatorColor="primary"
                textColor="inherit"
              >
                <Tab className={classes.tab} label={`Bases · ${listedGrantCount}`} />
                {!grantOnly && <Tab className={classes.tab} label="Settings" />}
              </Tabs>

              <div className={classes.tabBody}>
                {activeTab === TAB.BASES && (
                  <>
                    <Typography className={classes.hint}>
                    A company can only reach the bases granted here. Its users see all of them unless a
                    user is narrowed further in User Management.
                    </Typography>
                    <SelectorBases
                      fill
                      bases={bases}
                      selectedBaseIDs={grantedBaseIDs}
                      onChange={setGrantedBaseIDs}
                      emptyText="No bases matched."
                    />
                  </>
                )}

                {activeTab === TAB.SETTINGS && !grantOnly && (
                  <>
                    <Typography className={classes.subTitle}>Logo</Typography>
                    <div className={classes.block}>
                      <CompanyLogoPicker
                        inputId="companyDetailLogoInput"
                        companyID={selectedCompany._id}
                        logoUrl={selectedCompany.companyLogo}
                        createSnackbar={props.createSnackbar}
                        onUploaded={() => reload(selectedCompany._id)}
                      />
                    </div>

                    <Typography className={classes.subTitle}>PlayEngine loading screen</Typography>
                    <div className={classes.block}>
                      <Typography className={classes.hint}>
                      Every playEngine export this company downloads is built with this screen. It is
                      not offered in the export dialog, so the customer cannot change it themselves.
                      </Typography>
                      <TextField
                        select
                        size="small"
                        variant="outlined"
                        disabled={isSaving}
                        value={selectedCompany.playEngineLoadingScreen || "playinLoading"}
                        onChange={(event) => setLoadingScreen(event.target.value)}
                      >
                        {loadingScreenViews.map((screen) => (
                          <MenuItem key={screen.id} value={screen.id}>
                            {screen.caption}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>

                    <div className={classes.dangerBlock}>
                      <Typography className={classes.dangerTitle}>Internal tools</Typography>
                      <Typography className={classes.hint}>
                      Version Transfer and Version Update are Playin&apos;s own tools. Neither stays
                      inside a company: Version Update rewrites every version of whichever base it is
                      given and can be pointed at another environment&apos;s database. This also decides
                      who gets the Telegram bot, which reports agent sessions and starts them from a
                      phone. Leave this off for customers.
                      </Typography>
                      <div
                        className={classes.toggleRow}
                        onClick={() => !isSaving && setInternalTools(selectedCompany.hasInternalTools !== true)}
                      >
                        <Checkbox
                          size="small"
                          disabled={isSaving}
                          checked={selectedCompany.hasInternalTools === true}
                          onChange={(event) => setInternalTools(event.target.checked)}
                        />
                        <span className={classes.toggleLabel}>
                        Allow Version Transfer and Version Update
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className={classes.footer}>
                {activeTab === TAB.BASES && (
                  <>
                    <Button variant="contained" color="primary" disabled={isSaving} onClick={saveGrant}>
                    Save base grant
                    </Button>
                    <Button variant="outlined" onClick={() => setGrantedBaseIDs(selectedCompany.bases || [])}>
                    Reset
                    </Button>
                  </>
                )}
                <div className={classes.footerSpacer} />
                {canViewAs && (
                  <Button variant="outlined" onClick={viewAsCompany}>
                  View dashboard as this company
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
